/*
 * rageRoomController.js
 * -----------------------------------------------------------------------------
 * Controller factory for Rage Room API endpoints.
 *
 * Responsibilities:
 * - Encapsulates all admin and public rage room operations.
 * - Keeps endpoint-specific logic isolated from components/hooks.
 *
 * Usage:
 *   import createRageRoomController from "./controllers/rageRoomController";
 *   const rageRoom = createRageRoomController({ baseApiCall, parseQueryParams });
 *   await rageRoom.admin.listRooms({ locationId: "loc-123", query: { pageSize: 25 } });
 */

/**
 * Factory function that builds the Rage Room controller.
 *
 * @param {Object} deps - External dependencies.
 * @param {function(string, Object=): Promise<any>} deps.baseApiCall - Shared API caller.
 * @param {function(Object=): string} deps.parseQueryParams - Helper to stringify query params.
 * @returns {Object} Structured controller with admin and public namespaces.
 */
const createRageRoomController = ({ baseApiCall, parseQueryParams }) => {
	const adminBasePath = (locationId) => `/admin/locations/${locationId}/rage-rooms`;
	const schedulePath = (locationId, roomId) => `${adminBasePath(locationId)}/${roomId}/schedule`;
	const normalizeRoomPayload = (payload = {}) => {
		if (!payload || typeof payload !== 'object' || !Object.prototype.hasOwnProperty.call(payload, 'locationAddress')) {
			return payload;
		}
		const normalizedAddress = typeof payload.locationAddress === 'string'
			? payload.locationAddress
			: String(payload.locationAddress ?? '');
		return {
			...payload,
			locationAddress: normalizedAddress.trim()
		};
	};

	return {
		admin: {
			/**
			 * Creates a new rage room under a specific location.
			 *
			 * Backend: POST /admin/locations/:locationId/rage-rooms
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {Object} params.payload - Body payload describing the rage room.
			 */
			createRoom: async ({ locationId, payload }) => {
				return await baseApiCall(adminBasePath(locationId), {
					method: "POST",
					body: normalizeRoomPayload(payload)
				});
			},

			/**
			 * Lists rage rooms for a location with optional filters/pagination.
			 *
			 * Backend: GET /admin/locations/:locationId/rage-rooms
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {Object} [params.query] - Query parameters (pageNumber, city, isActive, etc.).
			 */
			listRooms: async ({ locationId, query }) => {
				const queryString = parseQueryParams(query);
				return await baseApiCall(`${adminBasePath(locationId)}${queryString}`);
			},

			/**
			 * Retrieves a single rage room.
			 *
			 * Backend: GET /admin/locations/:locationId/rage-rooms/:roomId
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {string} params.roomId - Room identifier.
			 */
			getRoom: async ({ locationId, roomId }) => {
				return await baseApiCall(`${adminBasePath(locationId)}/${roomId}`);
			},

			/**
			 * Updates an existing rage room.
			 *
			 * Backend: PUT /admin/locations/:locationId/rage-rooms/:roomId
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {string} params.roomId - Room identifier.
			 * @param {Object} params.payload - Fields to update.
			 */
			updateRoom: async ({ locationId, roomId, payload }) => {
				return await baseApiCall(`${adminBasePath(locationId)}/${roomId}`, {
					method: "PUT",
					body: normalizeRoomPayload(payload)
				});
			},

			/**
			 * Soft-deletes a rage room.
			 *
			 * Backend: DELETE /admin/locations/:locationId/rage-rooms/:roomId
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {string} params.roomId - Room identifier.
			 */
			deleteRoom: async ({ locationId, roomId }) => {
				return await baseApiCall(`${adminBasePath(locationId)}/${roomId}`, {
					method: "DELETE"
				});
			},

			/**
			 * Creates or updates a rage room schedule for a specific date.
			 *
			 * Backend: POST /admin/locations/:locationId/rage-rooms/:roomId/schedule
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {string} params.roomId - Room identifier.
			 * @param {Object} params.payload - Schedule payload (date, sessions, etc.).
			 */
			upsertSchedule: async ({ locationId, roomId, payload }) => {
				return await baseApiCall(schedulePath(locationId, roomId), {
					method: "POST",
					body: payload
				});
			},

			/**
			 * Lists schedules for a rage room.
			 *
			 * Backend: GET /admin/locations/:locationId/rage-rooms/:roomId/schedule
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {string} params.roomId - Room identifier.
			 * @param {Object} [params.query] - Query params (date, startDate, endDate, pagination).
			 */
			listSchedule: async ({ locationId, roomId, query }) => {
				const queryString = parseQueryParams(query);
				return await baseApiCall(`${schedulePath(locationId, roomId)}${queryString}`);
			},

			/**
			 * Lists bookings for a city/date span so admins can review slot-level customers.
			 *
			 * Backend: GET /admin/locations/:locationId/rage-rooms/bookings
			 *
			 * @param {Object} params
			 * @param {string} params.locationId - Location identifier.
			 * @param {Object} params.query - Query filters (city, date, pagination).
			 */
			listCityBookings: async ({ locationId, query }) => {
				const queryString = parseQueryParams(query);
				return await baseApiCall(`${adminBasePath(locationId)}/bookings${queryString}`);
			},

			/**
			 * Creates a booking on behalf of an admin user.
			 *
			 * Backend: POST /admin/rage-rooms/bookings
			 *
			 * @param {Object} params
			 * @param {Object} params.payload - Booking payload (locationId, roomId, session info, customer, etc.).
			 */
			createBooking: async ({ payload }) => {
				return await baseApiCall("/admin/rage-rooms/bookings", {
					method: "POST",
					body: payload
				});
			},

			/**
			 * Updates an existing booking on behalf of an admin user.
			 *
			 * Backend: PUT /admin/rage-rooms/bookings/:bookingId
			 *
			 * @param {Object} params
			 * @param {string} params.bookingId - Booking identifier.
			 * @param {Object} params.payload - Booking fields to update.
			 */
			updateBooking: async ({ bookingId, payload }) => {
				if (!bookingId) {
					throw new Error("bookingId is required to update a booking.");
				}
				return await baseApiCall(`/admin/rage-rooms/bookings/${bookingId}`, {
					method: "PUT",
					body: payload
				});
			},

			/**
			 * Resends a confirmation email for a specific booking.
			 *
			 * Backend: POST /admin/rage-rooms/bookings/:bookingId/resend-confirmation
			 *
			 * @param {Object} params
			 * @param {string} params.bookingId - Booking identifier.
			 */
			resendBookingConfirmationEmail: async ({ bookingId }) => {
				if (!bookingId) {
					throw new Error("bookingId is required to resend the confirmation email.");
				}
				return await baseApiCall(`/admin/rage-rooms/bookings/${bookingId}/resend-confirmation`, {
					method: "POST"
				});
			},

			/**
			 * Sends a reminder (or any other follow-up) email for a booking using a specific template.
			 *
			 * Backend: POST /admin/rage-rooms/bookings/:bookingId/send-reminder
			 *
			 * @param {Object} params
			 * @param {string} params.bookingId - Booking identifier.
			 * @param {string} params.templateName - Email template name to use.
			 */
			sendBookingReminderEmail: async ({ bookingId, templateName }) => {
				if (!bookingId) {
					throw new Error("bookingId is required to send a reminder email.");
				}
				if (!templateName || typeof templateName !== "string" || !templateName.trim()) {
					throw new Error("templateName is required to send a reminder email.");
				}
				return await baseApiCall(`/admin/rage-rooms/bookings/${bookingId}/send-reminder`, {
					method: "POST",
					body: {
						templateName: templateName.trim()
					}
				});
			},

			/**
			 * Notifies a waitlisted customer that a slot is available using a chosen email template.
			 *
			 * Backend: POST /admin/rage-rooms/waitlist/:waitlistId/notify-slot-available
			 *
			 * @param {Object} params
			 * @param {string} params.waitlistId - Waitlist entry identifier.
			 * @param {string} params.templateName - Email template to send.
			 */
			sendWaitlistSlotAvailableEmail: async ({ waitlistId, templateName }) => {
				if (!waitlistId) {
					throw new Error("waitlistId is required to send a slot availability email.");
				}
				if (!templateName || typeof templateName !== "string" || !templateName.trim()) {
					throw new Error("templateName is required to send a slot availability email.");
				}
				return await baseApiCall(`/admin/rage-rooms/waitlist/${waitlistId}/notify-slot-available`, {
					method: "POST",
					body: {
						templateName: templateName.trim()
					}
				});
			}
		},

		public: {
			/**
			 * Lists available rage room slots for public consumption.
			 *
			 * Backend: GET /rage-rooms/availability
			 *
			 * @param {Object} [query] - Filters such as city, roomId, date, etc.
			 */
			listAvailability: async (query) => {
				const queryString = parseQueryParams(query);
				return await baseApiCall(`/rage-rooms/availability${queryString}`);
			},

			/**
			 * Starts a Twilio verification for a given phone number.
			 *
			 * Backend: POST /rage-rooms/verify/start
			 *
			 * @param {Object} payload - Body payload containing the phone number.
			 */
			requestVerificationCode: async (payload) => {
				return await baseApiCall("/rage-rooms/verify/start", {
					method: "POST",
					body: payload
				});
			},

			/**
			 * Confirms a Twilio verification code and returns a signed token.
			 *
			 * Backend: POST /rage-rooms/verify/confirm
			 *
			 * @param {Object} payload - Phone number and verification code.
			 */
			verifyCustomer: async (payload) => {
				return await baseApiCall("/rage-rooms/verify/confirm", {
					method: "POST",
					body: payload
				});
			},

			/**
			 * Creates a public booking (requires verified token).
			 *
			 * Backend: POST /rage-rooms/bookings
			 *
			 * @param {Object} payload - Booking payload (locationId, roomId, date, session info, customer, etc.).
			 */
			bookRoom: async (payload) => {
				return await baseApiCall("/rage-rooms/bookings", {
					method: "POST",
					body: payload
				});
			},

			/**
			 * Joins the waitlist for a rage room when bookings are full.
			 *
			 * Backend: POST /rage-rooms/bookings/waitlist
			 *
			 * @param {Object} payload - Waitlist payload (locationId, roomId, optional date/time, customer, etc.).
			 */
			joinWaitlist: async (payload) => {
				return await baseApiCall("/rage-rooms/bookings/waitlist", {
					method: "POST",
					body: payload
				});
			},

			/**
			 * Lists waitlist entries for rage rooms with optional filters and pagination.
			 *
			 * Backend: GET /rage-rooms/waitlist
			 *
			 * @param {Object} [query] - Optional filters (locationId, roomId, status, date, pagination, etc.).
			 */
			listWaitlist: async (query) => {
				const queryString = parseQueryParams(query);
				return await baseApiCall(`/rage-rooms/waitlist${queryString}`);
			},

			/**
			 * Returns aggregated waitlist counts grouped by city for reporting dashboards.
			 *
			 * Backend: GET /rage-rooms/waitlist/summary
			 *
			 * @param {Object} [query] - Optional filters (locationId, status, date).
			 */
			listWaitlistSummary: async (query) => {
				const queryString = parseQueryParams(query);
				return await baseApiCall(`/rage-rooms/waitlist/summary${queryString}`);
			},

			/**
			 * Retrieves a single public booking by its identifier.
			 *
			 * Backend: GET /rage-rooms/bookings/:bookingId
			 *
			 * @param {Object} params
			 * @param {string} params.bookingId - Booking identifier.
			 */
			getBooking:	async ({ bookingId }) => {
				if (!bookingId) {
					throw new Error("bookingId is required to fetch a booking.");
				}
				return await baseApiCall(`/rage-rooms/bookings/${bookingId}`);
			},

			/**
			 * Cancels a booking via the public endpoint.
			 *
			 * Backend: POST /rage-rooms/bookings/:bookingId/cancel
			 *
			 * @param {Object} params
			 * @param {string} params.bookingId - Booking identifier.
			 * @param {Object} [params.payload] - Optional cancellation payload.
			 */
			cancelBooking: async ({ bookingId, payload }) => {
				if (!bookingId) {
					throw new Error("bookingId is required to cancel a booking.");
				}
				return await baseApiCall(`/rage-rooms/bookings/${bookingId}/cancel`, {
					method: "POST",
					body: payload
				});
			}
		}
	};
};

export default createRageRoomController;